/**
 * Decryption retry orchestrator.
 *
 * Triggers (no others, no timers, no on-render):
 *   1. Socket `connect` event
 *   2. `chatListSyncing` Zustand transitions true -> false
 *   3. Route change into a chat that has `failed` messages
 *   4. User taps "Retry" inside the report dialog
 *
 * Guarantees:
 * - Exponential backoff per attempt count (1s -> 16s, capped). Max 5
 *   attempts; after that the row stays in `failed` state and the UI shows
 *   the terminal "Unable to decrypt — tap to report".
 * - In-memory inflight lock prevents the same message from being retried
 *   twice in parallel when several triggers fire close together.
 * - On success, also updates `ChatEntity.lastMessage` if the retried
 *   message is the chat's current last message (denormalized copy).
 * - Gated behind `featureFlags.decryptionRetry`.
 */

import { db } from "@/indexedDB/db";
import {
  ChatEntity,
  MessageEntity,
  MessageContentEntity,
} from "@/indexedDB/types";
import {
  decryptMessageContent,
  decryptServiceMessageContent,
} from "@/middleware/crypto";
import {
  DecryptionError,
  isDecryptionError,
} from "@/middleware/decryptionError";
import { featureFlags } from "@/config/featureFlags";
import { createLogger } from "@/utils/debug";
import { getMessage } from "@/server/services/messageService";
import { MessageCategory } from "@/server/types/message";
import { awaitReconciliationIdleCrossTab } from "@/indexedDB/services/syncScheduler";
import { safeStorage } from "@/utils/safeStorage";

const log = createLogger("DecryptionRetry");

const MAX_ATTEMPTS = 5;
const BASE_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 16_000;
// Cap concurrent decrypt attempts so a backlog of N failed rows doesn't
// fan out into N parallel server fetches + crypto operations all at once.
const MAX_CONCURRENT_RETRIES = 4;

// --- Storm guard for the LEGACY (pre-v14, ciphertext-less) refetch class ------
// Pre-v14 `failed` rows discarded their ciphertext, so the ONLY recovery is a
// per-message server GET. On a real account with a large pre-v14 backlog that
// fans out into a refetch STORM — amplified because the in-memory cooldown maps
// (`lastAttemptedAt`/`transientFailures`) reset on every reload/tab, so a GLOBAL
// pass re-runs the whole backlog each time socket-connect / chatListSyncing
// fires. Two bounded guards (NO schema change, NO message loss):
//   1. A GLOBAL (unscoped) pass runs at most once per interval, throttled via a
//      localStorage timestamp that SURVIVES reload so reload/tab can't re-storm.
//   2. Per pass, the number of LEGACY server-refetches is capped; local-sidecar
//      decrypts (no network) stay unbounded. A large backlog drains gradually.
const GLOBAL_PASS_MIN_INTERVAL_MS = 5 * 60_000;
const GLOBAL_PASS_TS_KEY = "oa.decryptRetry.lastGlobalPassAt";
const LEGACY_REFETCH_BUDGET_PER_PASS = 20;

const readLastGlobalPassAt = (): number => {
  const v = safeStorage.get(GLOBAL_PASS_TS_KEY);
  const n = v ? Number(v) : 0;
  // Guard a corrupt/manipulated value: NaN/negative, or a FAR-FUTURE timestamp
  // that would otherwise suppress every global pass ~indefinitely. Treat any of
  // those as 0 (eligible now). 60s of clock skew is tolerated.
  if (!Number.isFinite(n) || n <= 0 || n > Date.now() + 60_000) {
    return 0;
  }
  return n;
};

const writeLastGlobalPassAt = (ts: number): void => {
  // safeStorage no-ops when localStorage is unavailable (SSR / private mode) —
  // the throttle simply degrades to "eligible now".
  safeStorage.set(GLOBAL_PASS_TS_KEY, String(ts));
};

/**
 * Returns the backoff (in ms) that should elapse since the previous attempt
 * before the next one is allowed. attempts is the number already made.
 *
 * 1 -> 1s, 2 -> 2s, 3 -> 4s, 4 -> 8s, 5 -> 16s (capped).
 */
export const computeBackoffMs = (attempts: number): number => {
  const safe = Math.max(1, attempts);
  const exp = Math.pow(2, safe - 1);
  return Math.min(BASE_BACKOFF_MS * exp, MAX_BACKOFF_MS);
};

const inflight = new Set<string>();
const lastAttemptedAt = new Map<string, number>();
// P3-d: per-row count of TRANSIENT failures (network/IDB), kept OFF the
// persisted `decryptionAttempts` row field. Drives an EXPANDING COOLDOWN
// (capped at MAX_BACKOFF_MS) so a flaky/unreachable row backs off instead of
// re-polling every window — WITHOUT a hard retire that would leave it stuck
// (even an explicit user Retry) until logout. Cleared when the row decrypts.
const transientFailures = new Map<string, number>();

/** Record one transient (non-decrypt) failure for a row — drives P3-d's cooldown. */
const bumpTransient = (id: string): void => {
  transientFailures.set(id, (transientFailures.get(id) ?? 0) + 1);
};

const isFailedRow = (msg: MessageEntity): boolean =>
  msg.decryptionStatus === "failed";

/**
 * Shared bookkeeping accessors so a sibling pass (G1 lazy-decrypt) can
 * coordinate with this orchestrator instead of double-processing rows:
 *   - `isRowInflight` — true while THIS orchestrator is mid-attempt on the row,
 *     so the sibling pass can skip it (no double-decrypt in one runSync).
 *   - `MAX_ATTEMPTS` (re-exported as `DECRYPT_MAX_ATTEMPTS`) — the single
 *     terminal-state cap, reused by the sibling pass so a permanently-bad row
 *     reaches the SAME terminal "Unable to decrypt" state instead of being
 *     retried forever from a second entry point.
 */
export const isRowInflight = (id: string): boolean => inflight.has(id);
export const DECRYPT_MAX_ATTEMPTS = MAX_ATTEMPTS;

/** Per-pass observability counters (§3.5). */
interface RetryPassMetrics {
  sidecarDecrypted: number; // healed from the local v14 ciphertext (no network)
  legacyRefetched: number; // legacy rows with no sidecar → server refetch
}

/**
 * Attempt to retry a single message. Returns true if the retry succeeded.
 * Caller is responsible for honoring backoff windows and inflight locks.
 */
const attemptRetry = async (
  msg: MessageEntity,
  metrics: RetryPassMetrics,
): Promise<boolean> => {
  const chat = await db.chats.get(msg.chatId);
  if (!chat) {
    // Chat row not synced yet — transient. Count it (P3-d) so the row's cooldown
    // EXPANDS instead of re-polling every ~1s until the chat arrives. NOT a
    // decrypt failure, so decryptionAttempts is untouched.
    bumpTransient(msg.id);
    return false;
  }

  // Service messages are base64-JSON envelopes — no eCSK needed. Regular
  // (and comment) messages do need it; if it isn't synced yet that's the most
  // common transient — treat it like the missing-chat case above (expanding
  // cooldown, decryptionAttempts untouched).
  const isService = msg.category === MessageCategory.SERVICE;
  if (!isService && !chat.eCSK) {
    bumpTransient(msg.id);
    return false;
  }

  const nextAttempts = (msg.decryptionAttempts ?? 0) + 1;
  if (nextAttempts > MAX_ATTEMPTS) {
    return false;
  }

  try {
    let decrypted: MessageContentEntity;

    if (msg.ciphertext) {
      // P3-RESIDUAL: decrypt from the LOCAL ciphertext sidecar (v14) — NO
      // network. This is the path for every row PR-1's backfill (or the WS
      // path) stored with retained ciphertext, which is the overwhelming
      // majority — so the per-message GET storm is gone.
      metrics.sidecarDecrypted += 1;
      if (isService) {
        decrypted = await decryptServiceMessageContent({
          content: msg.ciphertext.content,
          sender: msg.sender,
        } as Parameters<typeof decryptServiceMessageContent>[0]);
      } else {
        decrypted = await decryptMessageContent(
          msg.chatId,
          msg.keyEpoch ?? 0,
          chat.eCSK,
          msg.ciphertext.content,
          msg.ciphertext.attachmentMeta ?? undefined,
        );
      }
    } else {
      // Legacy `failed` rows that predate the v14 sidecar discarded their
      // ciphertext, so the ONLY recovery is a server refetch. Reachable only
      // for those rows; capped by the worker pool + backoff below. (Per pre-work
      // U1, this hits the Rust gateway, which has NO rate limiter — so there is
      // no server 429/Retry-After to honor here; the client-side cap IS the
      // protection.)
      //
      // Phase-1 cold-start fix: this `getMessage` routes through the SHARED sync
      // scheduler (`retry429: true`). On a cold start / SW update this pass fires
      // on the SAME socket-connect event as `runSync`, so its refetches would
      // drain the shared token bucket WHILE the chat-list reconciliation is
      // mid-traversal — exactly the starvation that lost the CEO's chat list.
      // Defer the network refetch until the reconciliation finishes (or the
      // safety-valve timeout). Local-sidecar decrypts (the common path above) make
      // NO network call and are NOT gated. The await resolves immediately when no
      // reconciliation is running, so steady-state is unaffected.
      //
      // #958: the per-tab gate alone is defeated with 2+ tabs (a lock-skipped
      // tab drops its gate while ANOTHER tab is mid-traversal). The cross-tab
      // variant ALSO defers while a sync is observably running in another tab
      // (via `navigator.locks.query()` on the sync lock), so this refetch can't
      // starve a different tab's chat-list traversal either.
      await awaitReconciliationIdleCrossTab();
      metrics.legacyRefetched += 1;
      const encrypted = await getMessage({
        message_id: msg.id,
        chat_id: msg.chatId,
        category: msg.category,
      });

      if (!encrypted) {
        throw new DecryptionError("CIPHERTEXT_DECRYPT_FAILED");
      }

      if (isService) {
        decrypted = await decryptServiceMessageContent(encrypted);
      } else {
        decrypted = await decryptMessageContent(
          msg.chatId,
          encrypted.key_epoch ?? msg.keyEpoch ?? 0,
          chat.eCSK,
          encrypted.content,
          encrypted.attachment_meta,
        );
      }
    }

    await db.messages.update(msg._clientId, {
      content: decrypted,
      decryptionStatus: "ok",
      decryptionAttempts: nextAttempts,
      // Clear the at-rest ciphertext now that the row is decrypted (v14
      // lifecycle — bound how long encrypted payloads linger locally).
      ciphertext: undefined,
    });
    transientFailures.delete(msg.id);

    // Denormalized copy on the chat row — repair it too. If the retried
    // message is the chat's `lastMessage`, also bump the timestamp so the
    // chat-list ordering is correct.
    if (chat.lastMessage?.id === msg.id) {
      await db.chats.update(chat.id, {
        lastMessage: {
          ...chat.lastMessage,
          content: decrypted,
          decryptionStatus: "ok",
          decryptionAttempts: nextAttempts,
          // Clear any stale sidecar carried on the denormalized copy too, so it
          // matches the cleared `messages` row (v14 lifecycle).
          ciphertext: undefined,
        } satisfies MessageEntity,
        lastMessageTimestamp: msg.timestamp,
      } satisfies Partial<ChatEntity>);
    }

    return true;
  } catch (error) {
    // Only burn a retry-budget attempt for actual decryption failures.
    // Transient errors (network from `getMessage`, IDB blip, etc.) leave
    // `decryptionAttempts` untouched so connectivity recovery can heal the
    // row. The `lastAttemptedAt` map still throttles via the backoff
    // window, so we don't hammer the API while offline.
    const isDecryptError = isDecryptionError(error);
    const reason = isDecryptError ? error.reason : "TRANSIENT";
    log.warn("Retry attempt failed", { reason, attempts: nextAttempts });

    if (isDecryptError) {
      // Genuine decryption failure — burn a retry-budget attempt. After
      // MAX_ATTEMPTS the row goes terminal ("Unable to decrypt — tap to report").
      await db.messages.update(msg._clientId, {
        decryptionAttempts: nextAttempts,
      });
    } else {
      // P3-d: transient (network/IDB) — do NOT touch decryptionAttempts (so
      // connectivity recovery can still heal the row), but count it so the
      // ready-filter expands this row's cooldown rather than re-polling an
      // unreachable row every window.
      bumpTransient(msg.id);
    }
    return false;
  }
};

/**
 * Run a retry pass over all currently failed messages. Optional `chatId`
 * narrows the scope (used by the route-change trigger).
 */
export const runDecryptionRetryPass = async (
  scope?: string | string[],
  opts?: { force?: boolean },
): Promise<void> => {
  if (!featureFlags.decryptionRetry) {
    return;
  }

  // P3-c: the post-event sync trigger passes the chats CHANGED this run so the
  // pass scopes to them instead of scanning every failed row in the account.
  // Other triggers pass a single chatId (route-change) or nothing (socket
  // connect / report-dialog Retry → the global scan).
  const chatIds = scope == null ? [] : Array.isArray(scope) ? scope : [scope];

  // Storm guard (1): throttle the GLOBAL (unscoped) backlog sweep across reloads.
  // Scoped passes (route-into-chat / post-event) stay fully responsive; only the
  // unscoped full-backlog sweep is rate-limited so socket-connect / reload / new
  // tab can't re-storm the whole pre-v14 legacy backlog. The timestamp lives in
  // localStorage so it survives reload (the in-memory maps don't). `force`
  // bypasses the throttle and is RESERVED for an explicit user-retry path — NOT
  // yet wired to any production caller (see follow-up); today only tests pass it.
  if (chatIds.length === 0 && !opts?.force) {
    if (Date.now() - readLastGlobalPassAt() < GLOBAL_PASS_MIN_INTERVAL_MS) {
      return;
    }
    writeLastGlobalPassAt(Date.now());
  }

  let candidates: MessageEntity[];
  try {
    // Indexed lookup. The scoped path uses the `chatId` index and post-filters
    // by status; the global path uses the `decryptionStatus` index added in db
    // v12 to avoid scanning the whole `messages` table.
    if (chatIds.length === 1) {
      candidates = await db.messages
        .where("chatId")
        .equals(chatIds[0])
        .filter(isFailedRow)
        .toArray();
    } else if (chatIds.length > 1) {
      candidates = await db.messages
        .where("chatId")
        .anyOf(chatIds)
        .filter(isFailedRow)
        .toArray();
    } else {
      candidates = await db.messages
        .where("decryptionStatus")
        .equals("failed")
        .toArray();
    }
  } catch (error) {
    log.error("Could not enumerate failed messages", error);
    return;
  }

  // Review cleanup: on a GLOBAL pass we've enumerated EVERY failed row, so any
  // id still tracked in the in-memory maps that is no longer failed (the row
  // healed via another path — e.g. the WS inline-decrypt — or was deleted) is
  // stale. Prune it so `transientFailures`/`lastAttemptedAt` stay bounded by the
  // live failed backlog instead of growing for the whole session. (SCOPED passes
  // only see a subset of failed rows, so they must NOT prune — an untracked id
  // there may still be failed in another chat.) Logout still hard-clears both.
  if (chatIds.length === 0) {
    const stillFailed = new Set(candidates.map((m) => m.id));
    for (const id of transientFailures.keys()) {
      if (!stillFailed.has(id)) {
        transientFailures.delete(id);
      }
    }
    for (const id of lastAttemptedAt.keys()) {
      if (!stillFailed.has(id)) {
        lastAttemptedAt.delete(id);
      }
    }
  }

  if (candidates.length === 0) {
    return;
  }

  const now = Date.now();
  const ready = candidates.filter((m) => {
    const attempts = m.decryptionAttempts ?? 0;
    if (attempts >= MAX_ATTEMPTS) {
      return false;
    }
    if (inflight.has(m.id)) {
      return false;
    }
    const transientCount = transientFailures.get(m.id) ?? 0;
    const last = lastAttemptedAt.get(m.id);
    if (last !== undefined) {
      // P3-d expanding cooldown: widen the backoff window on transient failures
      // too, not just genuine decrypt failures — so a flaky-network row backs
      // off (1s → 16s, capped) instead of re-polling every 1s while its attempts
      // stay flat. NOT a hard retire: once the cooled-down window elapses the row
      // is eligible again, so connectivity recovery (or a user Retry) heals it.
      const wait = computeBackoffMs(Math.max(attempts, transientCount));
      if (now - last < wait) {
        return false;
      }
    }
    return true;
  });

  if (ready.length === 0) {
    return;
  }

  // Storm guard (2): cap the number of LEGACY (ciphertext-less → per-message
  // server GET) rows refetched per pass. Local-sidecar decrypts (no network)
  // stay unbounded. Rows beyond the budget keep their `failed` status and are
  // picked up by a later pass, so a large pre-v14 backlog drains gradually
  // instead of fanning out into a per-message refetch storm.
  const localReady = ready.filter((m) => m.ciphertext != null);
  const legacyReady = ready.filter((m) => m.ciphertext == null);
  const legacyDeferred = Math.max(
    0,
    legacyReady.length - LEGACY_REFETCH_BUDGET_PER_PASS,
  );
  const worklist =
    legacyDeferred > 0
      ? [...localReady, ...legacyReady.slice(0, LEGACY_REFETCH_BUDGET_PER_PASS)]
      : ready;

  // Worker-pool: cap concurrent retries to MAX_CONCURRENT_RETRIES so a
  // backlog of failures doesn't fan out into a thundering-herd of decrypt work
  // (and, for legacy rows, server fetches).
  const metrics: RetryPassMetrics = { sidecarDecrypted: 0, legacyRefetched: 0 };
  let cursor = 0;
  const worker = async (): Promise<void> => {
    while (cursor < worklist.length) {
      const msg = worklist[cursor++];
      inflight.add(msg.id);
      lastAttemptedAt.set(msg.id, Date.now());
      try {
        await attemptRetry(msg, metrics);
      } finally {
        inflight.delete(msg.id);
      }
    }
  };

  const workerCount = Math.min(MAX_CONCURRENT_RETRIES, worklist.length);
  await Promise.all(Array.from({ length: workerCount }, worker));

  // §3.5 observability: prove the storm is gone — sidecar decrypts (no network)
  // should dominate; legacy refetches should trend to ~0 as old `failed` rows
  // age out.
  log.info("[decryptionRetry] pass complete", {
    ready: ready.length,
    processed: worklist.length,
    legacyDeferred,
    sidecarDecrypted: metrics.sidecarDecrypted,
    legacyRefetched: metrics.legacyRefetched,
  });
};

/**
 * Clear all in-memory orchestrator state. Call this on logout so retry
 * bookkeeping for one user doesn't leak into the next session in the same
 * tab.
 */
export const clearDecryptionRetryState = (): void => {
  inflight.clear();
  lastAttemptedAt.clear();
  transientFailures.clear();
  // Reset the global-pass throttle so a fresh session (new login in the same
  // tab) can run its first backlog sweep immediately.
  writeLastGlobalPassAt(0);
};

// ── Test-only helpers ───────────────────────────────────────────────────────

export const _MAX_ATTEMPTS = MAX_ATTEMPTS;
export const _MAX_CONCURRENT_RETRIES = MAX_CONCURRENT_RETRIES;

/** Reset internal locks/state — tests only. */
export const _resetRetryOrchestrator = (): void => {
  inflight.clear();
  lastAttemptedAt.clear();
  transientFailures.clear();
  writeLastGlobalPassAt(0);
};

/** Snapshot of the inflight set — tests only. */
export const _getInflightSnapshot = (): string[] => Array.from(inflight);

/** Mark a row inflight to exercise cross-pass coordination — tests only. */
export const _setInflight = (id: string): void => {
  inflight.add(id);
};

/** Transient-failure count for a row (P3-d) — tests only. */
export const _getTransientCount = (id: string): number =>
  transientFailures.get(id) ?? 0;

// Re-export for convenience.
export { DecryptionError };
